import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Modal,
  Space,
  Tag,
  Card,
  message,
  Popconfirm,
  Drawer,
  Typography
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { getEmployees, updateEmployee, deleteEmployee, addEmployee } from '../../../api/supabase';
import dayjs from 'dayjs';
import theme from '../../../theme/globalTheme';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

const EmployeeListPage = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    handleSearch(searchText);
  }, [employees, searchText]);

  const fetchEmployees = async () => {
    setLoading(true);
    const { data, error } = await getEmployees();
    if (error) {
      message.error(error.message);
    } else {
      setEmployees(data);
    }
    setLoading(false);
  };

  const handleSearch = (value) => {
    const searchValue = value.toLowerCase().trim();
    if (!searchValue) {
      setFilteredEmployees(employees);
      return;
    }

    const filtered = employees.filter((employee) => {
      const name = employee.name?.toLowerCase() || '';
      const firstName = employee.first_name?.toLowerCase() || '';
      const role = employee.role?.toLowerCase() || '';
      const status = employee.status?.toLowerCase() || '';
      const languages = employee.languages?.join(' ').toLowerCase() || '';

      return (
        name.includes(searchValue) ||
        firstName.includes(searchValue) ||
        role.includes(searchValue) ||
        status.includes(searchValue) ||
        languages.includes(searchValue)
      );
    });

    setFilteredEmployees(filtered);
  };

  const handleDelete = async (id) => {
    const { error } = await deleteEmployee(id);
    if (error) {
      message.error(error.message);
    } else {
      message.success('Employee deleted successfully');
      setEmployees(employees.filter((employee) => employee.id !== id));
    }
  };

  const showEditModal = (employee) => {
    setCurrentEmployee(employee);
    setIsEditing(true);
    editForm.setFieldsValue({
      ...employee,
      first_name: employee.first_name || '',
      starting_date: employee.starting_date ? dayjs(employee.starting_date) : null,
      languages: employee.languages ? employee.languages.join(', ') : '',
      aliases: employee.aliases ? employee.aliases.join(', ') : ''
    });
  };

  const showAddDrawer = () => {
    form.resetFields();
    setIsAddDrawerOpen(true);
  };

  const handleUpdate = async (values) => {
    const updatedFields = {
      ...values,
      first_name: values.first_name || null,
      starting_date: values.starting_date ? values.starting_date.format('YYYY-MM-DD') : null,
      languages: values.languages ? values.languages.split(',').map(lang => lang.trim()) : [],
      aliases: values.aliases ? values.aliases.split(',').map(alias => alias.trim()) : []
    };

    const { data, error } = await updateEmployee(currentEmployee.id, updatedFields);
    if (error) {
      message.error(error.message);
    } else {
      message.success('Employee updated successfully');
      setEmployees(employees.map((emp) => (emp.id === currentEmployee.id ? data[0] : emp)));
      setIsEditing(false);
    }
  };

  const handleAdd = async (values) => {
    const employeeToAdd = {
      ...values,
      first_name: values.first_name || null,
      starting_date: values.starting_date ? values.starting_date.format('YYYY-MM-DD') : null,
      languages: values.languages ? values.languages.split(',').map(lang => lang.trim()) : [],
      aliases: values.aliases ? values.aliases.split(',').map(alias => alias.trim()) : []
    };

    const { data, error } = await addEmployee(employeeToAdd);
    if (error) {
      message.error(error.message);
    } else {
      message.success('Employee added successfully');
      setEmployees([...employees, data[0]]);
      form.resetFields();
      setIsAddDrawerOpen(false);
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontWeight: theme.typography.fontWeight.bold,
            fontSize: theme.typography.fontSize.base,
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
          }}>
            {name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <Text strong style={{ color: theme.colors.textPrimary }}>
            {name}
          </Text>
        </div>
      )
    },
    {
      title: 'Prénom',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 140,
      render: (firstName) => (
        <Text style={{ color: theme.colors.textPrimary }}>
          {firstName || '-'}
        </Text>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 160,
      render: (role) => {
        const roleColors = {
          'Manager': { color: '#722ed1', icon: '👔' },
          'Skipper': { color: '#1890ff', icon: '⚓' },
          'Hostess': { color: '#eb2f96', icon: '🎀' },
          'Engineer': { color: '#52c41a', icon: '🔧' },
          'Chef': { color: '#fa8c16', icon: '👨‍🍳' },
          'Crew': { color: '#13c2c2', icon: '⛵' }
        };
        const config = roleColors[role] || { color: '#8c8c8c', icon: '👤' };
        return (
          <Tag
            style={{
              background: `${config.color}15`,
              borderColor: config.color,
              color: config.color,
              fontWeight: theme.typography.fontWeight.medium,
              padding: '4px 12px',
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {config.icon} {role}
          </Tag>
        );
      }
    },
    {
      title: 'Starting Date',
      dataIndex: 'starting_date',
      key: 'starting_date',
      width: 140,
      render: (date) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: theme.borderRadius.sm,
            background: theme.colors.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
          }}>
            📅
          </div>
          <Text style={{ color: theme.colors.textPrimary, fontSize: theme.typography.fontSize.sm }}>
            {date ? dayjs(date).format('MM/DD/YYYY') : 'N/A'}
          </Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag
          style={{
            background: status === 'active' ? theme.colors.successLight : '#f5f5f5',
            borderColor: status === 'active' ? theme.colors.success : '#d9d9d9',
            color: status === 'active' ? theme.colors.success : theme.colors.textSecondary,
            fontWeight: theme.typography.fontWeight.semibold,
            padding: '4px 12px',
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSize.sm,
          }}
        >
          {status === 'active' ? '✓' : '○'} {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Languages',
      dataIndex: 'languages',
      key: 'languages',
      width: 200,
      render: (languages) => (
        <Space size={[4, 4]} wrap>
          {languages?.length > 0 ? languages.map((lang, index) => {
            const languageColors = {
              'English': '#1890ff',
              'French': '#ff7a45',
              'Spanish': '#faad14',
              'Italian': '#52c41a',
              'German': '#722ed1',
              'Portuguese': '#eb2f96'
            };
            const color = languageColors[lang] || '#8c8c8c';
            return (
              <Tag
                key={index}
                style={{
                  background: `${color}15`,
                  borderColor: color,
                  color: color,
                  fontWeight: theme.typography.fontWeight.medium,
                  padding: '2px 8px',
                  borderRadius: theme.borderRadius.sm,
                  fontSize: theme.typography.fontSize.xs,
                }}
              >
                {lang}
              </Tag>
            );
          }) : <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>N/A</Text>}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            size="small"
            style={{
              borderColor: '#1890ff',
              color: '#1890ff',
              fontWeight: theme.typography.fontWeight.medium,
              borderRadius: theme.borderRadius.sm,
              padding: '4px 12px',
              height: 'auto',
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete employee"
            description="Are you sure you want to delete this employee?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                background: '#ff4d4f',
                borderColor: '#ff4d4f',
                color: '#ffffff',
                fontWeight: theme.typography.fontWeight.medium,
                borderRadius: theme.borderRadius.sm,
                padding: '4px 12px',
                height: 'auto',
              }}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const EmployeeForm = ({ form, onFinish, submitText, onCancel }) => (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      style={{
        background: theme.colors.bgPrimary,
      }}
    >
      {/* Name and First Name Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
        <Form.Item
          name="name"
          label={
            <Text strong style={{
              color: theme.colors.textPrimary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold
            }}>
              Nom de famille
            </Text>
          }
          rules={[{ required: true, message: 'Le nom est obligatoire' }]}
          style={{ marginBottom: theme.spacing.lg }}
        >
          <Input
            placeholder="Nom de famille"
            size="large"
            style={{
              borderRadius: theme.borderRadius.lg,
              border: `2px solid ${theme.colors.border}`,
              fontSize: theme.typography.fontSize.base,
              padding: '12px 16px',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
            }}
            onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
            onBlur={(e) => e.target.style.borderColor = theme.colors.border}
          />
        </Form.Item>

        <Form.Item
          name="first_name"
          label={
            <Text strong style={{
              color: theme.colors.textPrimary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold
            }}>
              Prénom
            </Text>
          }
          style={{ marginBottom: theme.spacing.lg }}
        >
          <Input
            placeholder="Prénom"
            size="large"
            style={{
              borderRadius: theme.borderRadius.lg,
              border: `2px solid ${theme.colors.border}`,
              fontSize: theme.typography.fontSize.base,
              padding: '12px 16px',
              transition: 'all 0.3s ease',
            }}
            onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
            onBlur={(e) => e.target.style.borderColor = theme.colors.border}
          />
        </Form.Item>
      </div>

      {/* Aliases Field */}
      <Form.Item
        name="aliases"
        label={
          <Text strong style={{
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold
          }}>
            Alias (variantes du nom)
          </Text>
        }
        extra={
          <Text type="secondary" style={{
            fontSize: theme.typography.fontSize.xs,
            fontStyle: 'italic',
            marginTop: '4px'
          }}>
            Séparez par des virgules (ex: ANTHO, ANTO, Tony) - Utilisé pour la détection dans les plannings PDF
          </Text>
        }
        style={{ marginBottom: theme.spacing.lg }}
      >
        <Input.TextArea
          placeholder="ANTHO, ANTO, Tony..."
          rows={2}
          style={{
            borderRadius: theme.borderRadius.lg,
            border: `2px solid ${theme.colors.border}`,
            fontSize: theme.typography.fontSize.base,
            padding: '12px 16px',
            resize: 'none',
            transition: 'all 0.3s ease',
          }}
          onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
          onBlur={(e) => e.target.style.borderColor = theme.colors.border}
        />
      </Form.Item>

      {/* Role Field */}
      <Form.Item
        name="role"
        label={
          <Text strong style={{
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold
          }}>
            Rôle
          </Text>
        }
        rules={[{ required: true, message: 'Le rôle est obligatoire' }]}
        style={{ marginBottom: theme.spacing.lg }}
      >
        <Input
          placeholder="Entrez le rôle (ex: Manager, Skipper, Hostess...)"
          size="large"
          style={{
            borderRadius: theme.borderRadius.lg,
            border: `2px solid ${theme.colors.border}`,
            fontSize: theme.typography.fontSize.base,
            padding: '12px 16px',
            transition: 'all 0.3s ease',
          }}
          onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
          onBlur={(e) => e.target.style.borderColor = theme.colors.border}
        />
      </Form.Item>

      {/* Two Column Layout for Date and Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
        {/* Starting Date Field */}
        <Form.Item
          name="starting_date"
          label={
            <Text strong style={{
              color: theme.colors.textPrimary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold
            }}>
              Date de début
            </Text>
          }
          style={{ marginBottom: theme.spacing.lg }}
        >
          <DatePicker
            placeholder="Sélectionner"
            format="DD/MM/YYYY"
            size="large"
            style={{
              width: '100%',
              borderRadius: theme.borderRadius.lg,
              border: `2px solid ${theme.colors.border}`,
              fontSize: theme.typography.fontSize.base,
            }}
          />
        </Form.Item>

        {/* Status Field */}
        <Form.Item
          name="status"
          label={
            <Text strong style={{
              color: theme.colors.textPrimary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold
            }}>
              Statut
            </Text>
          }
          initialValue="active"
          style={{ marginBottom: theme.spacing.lg }}
        >
          <Select
            size="large"
            style={{
              borderRadius: theme.borderRadius.lg,
            }}
            dropdownStyle={{
              borderRadius: theme.borderRadius.lg,
              boxShadow: theme.effects.shadowLg,
            }}
          >
            <Option value="active">
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: theme.colors.success,
                }}></div>
                <span>Actif</span>
              </div>
            </Option>
            <Option value="inactive">
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#8c8c8c',
                }}></div>
                <span>Inactif</span>
              </div>
            </Option>
          </Select>
        </Form.Item>
      </div>

      {/* Languages Field */}
      <Form.Item
        name="languages"
        label={
          <Text strong style={{
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold
          }}>
            Langues parlées
          </Text>
        }
        extra={
          <Text type="secondary" style={{
            fontSize: theme.typography.fontSize.xs,
            fontStyle: 'italic',
            marginTop: '4px'
          }}>
            Séparez par des virgules (ex: English, French, Spanish)
          </Text>
        }
        style={{ marginBottom: theme.spacing.xl }}
      >
        <Input.TextArea
          placeholder="English, French, Spanish, Italian..."
          rows={2}
          style={{
            borderRadius: theme.borderRadius.lg,
            border: `2px solid ${theme.colors.border}`,
            fontSize: theme.typography.fontSize.base,
            padding: '12px 16px',
            resize: 'none',
            transition: 'all 0.3s ease',
          }}
          onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
          onBlur={(e) => e.target.style.borderColor = theme.colors.border}
        />
      </Form.Item>

      {/* Submit Buttons */}
      <Form.Item style={{ marginBottom: 0, marginTop: theme.spacing.xl }}>
        <div style={{
          display: 'flex',
          gap: theme.spacing.md,
          justifyContent: 'flex-end',
          paddingTop: theme.spacing.lg,
          borderTop: `2px solid ${theme.colors.border}`,
        }}>
          <Button
            onClick={onCancel}
            size="large"
            style={{
              borderRadius: theme.borderRadius.lg,
              minWidth: '120px',
              height: '44px',
              fontWeight: theme.typography.fontWeight.medium,
              fontSize: theme.typography.fontSize.base,
              border: `2px solid ${theme.colors.border}`,
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.colors.textSecondary;
              e.currentTarget.style.color = theme.colors.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.colors.border;
              e.currentTarget.style.color = '';
            }}
          >
            Annuler
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            style={{
              minWidth: '160px',
              height: '44px',
              fontWeight: theme.typography.fontWeight.semibold,
              fontSize: theme.typography.fontSize.base,
              borderRadius: theme.borderRadius.lg,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
            }}
          >
            {submitText}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );

  // Calculate statistics
  const activeEmployees = employees.filter(emp => emp.status === 'active').length;
  const inactiveEmployees = employees.filter(emp => emp.status === 'inactive').length;

  return (
    <div style={{
      padding: theme.spacing.md,
      maxWidth: '100%',
      minHeight: '100vh',
      background: theme.colors.bgSecondary,
    }}>
      {/* Compact Sticky Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.md,
        background: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.effects.shadowMd,
        border: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: theme.borderRadius.md,
            background: theme.colors.primaryLight,
            border: `1px solid ${theme.colors.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: theme.effects.shadowSm,
          }}>
            👥
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
              Gestion des employés
            </Title>
            <Text style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
              {filteredEmployees.length} employé{filteredEmployees.length > 1 ? 's' : ''}
            </Text>
          </div>
        </div>

        <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
          <Search
            placeholder="Rechercher par nom, rôle, statut..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchEmployees}
            style={{
              borderRadius: theme.borderRadius.md,
              boxShadow: theme.effects.shadowSm,
            }}
          >
            Actualiser
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddDrawer}
            style={{
              height: '40px',
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              borderRadius: theme.borderRadius.md,
              boxShadow: theme.effects.shadowMd,
            }}
          >
            Ajouter un employé
          </Button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing.md,
        marginBottom: theme.spacing.md,
      }}>
        {/* Total Employees */}
        <Card
          style={{
            border: `1px solid ${theme.colors.border}`,
            borderLeft: `4px solid #1890ff`,
            boxShadow: theme.effects.shadowSm,
            background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.05) 0%, rgba(24, 144, 255, 0.02) 100%)',
          }}
          styles={{ body: { padding: theme.spacing.md } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: theme.borderRadius.md,
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
            }}>
              <TeamOutlined style={{ color: '#ffffff', fontSize: '20px' }} />
            </div>
            <div>
              <Text style={{ display: 'block', fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                Total Employés
              </Text>
              <Text strong style={{ fontSize: theme.typography.fontSize.xl, color: '#1890ff' }}>
                {employees.length}
              </Text>
            </div>
          </div>
        </Card>

        {/* Active Employees */}
        <Card
          style={{
            border: `1px solid ${theme.colors.border}`,
            borderLeft: `4px solid ${theme.colors.success}`,
            boxShadow: theme.effects.shadowSm,
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
          }}
          styles={{ body: { padding: theme.spacing.md } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: theme.borderRadius.md,
              background: `linear-gradient(135deg, ${theme.colors.success} 0%, #059669 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: `0 4px 12px rgba(16, 185, 129, 0.3)`,
            }}>
              ✓
            </div>
            <div>
              <Text style={{ display: 'block', fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                Actifs
              </Text>
              <Text strong style={{ fontSize: theme.typography.fontSize.xl, color: theme.colors.success }}>
                {activeEmployees}
              </Text>
            </div>
          </div>
        </Card>

        {/* Inactive Employees */}
        <Card
          style={{
            border: `1px solid ${theme.colors.border}`,
            borderLeft: `4px solid #8c8c8c`,
            boxShadow: theme.effects.shadowSm,
            background: 'linear-gradient(135deg, rgba(140, 140, 140, 0.05) 0%, rgba(140, 140, 140, 0.02) 100%)',
          }}
          styles={{ body: { padding: theme.spacing.md } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: theme.borderRadius.md,
              background: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(140, 140, 140, 0.3)',
            }}>
              ○
            </div>
            <div>
              <Text style={{ display: 'block', fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                Inactifs
              </Text>
              <Text strong style={{ fontSize: theme.typography.fontSize.xl, color: '#8c8c8c' }}>
                {inactiveEmployees}
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* Employees Table */}
      <Card
        style={{
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <Table
          columns={columns}
          dataSource={filteredEmployees}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} employés`,
          }}
        />
      </Card>

      <Drawer
        title={
          <div style={{ padding: theme.spacing.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: theme.borderRadius.lg,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              }}>
                <PlusOutlined style={{ fontSize: '22px', color: '#ffffff' }} />
              </div>
              <div>
                <Text strong style={{
                  color: theme.colors.textPrimary,
                  fontSize: '20px',
                  display: 'block',
                  fontWeight: 700,
                  marginBottom: '4px'
                }}>
                  Nouvel employé
                </Text>
                <Text style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary,
                  display: 'block'
                }}>
                  Ajoutez un nouveau membre à votre équipe
                </Text>
              </div>
            </div>
          </div>
        }
        placement="right"
        onClose={() => {
          form.resetFields();
          setIsAddDrawerOpen(false);
        }}
        open={isAddDrawerOpen}
        width={560}
        styles={{
          body: {
            padding: theme.spacing.xl,
            background: '#f8f9fa',
          },
          header: {
            borderBottom: `2px solid ${theme.colors.border}`,
            padding: theme.spacing.lg,
            background: '#ffffff',
          }
        }}
        closeIcon={
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            color: theme.colors.textSecondary,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e0e0e0';
            e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f0f0f0';
            e.currentTarget.style.transform = 'rotate(0deg)';
          }}
          >
            ✕
          </div>
        }
      >
        <div style={{
          background: '#ffffff',
          borderRadius: theme.borderRadius.xl,
          padding: theme.spacing.xl,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <EmployeeForm
            form={form}
            onFinish={handleAdd}
            submitText="Ajouter l'employé"
            onCancel={() => {
              form.resetFields();
              setIsAddDrawerOpen(false);
            }}
          />
        </div>
      </Drawer>

      <Modal
        title={
          <div style={{ padding: theme.spacing.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: theme.borderRadius.lg,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              }}>
                <EditOutlined style={{ fontSize: '22px', color: '#ffffff' }} />
              </div>
              <div>
                <Text strong style={{
                  color: theme.colors.textPrimary,
                  fontSize: '20px',
                  display: 'block',
                  fontWeight: 700,
                  marginBottom: '4px'
                }}>
                  Modifier l'employé
                </Text>
                <Text style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary,
                  display: 'block'
                }}>
                  {currentEmployee?.name}
                </Text>
              </div>
            </div>
          </div>
        }
        open={isEditing}
        onCancel={() => {
          editForm.resetFields();
          setIsEditing(false);
          setCurrentEmployee(null);
        }}
        footer={null}
        width={640}
        centered
        styles={{
          body: {
            padding: theme.spacing.xl,
            background: '#f8f9fa',
          },
          header: {
            borderBottom: `2px solid ${theme.colors.border}`,
            padding: theme.spacing.lg,
            background: '#ffffff',
          }
        }}
        closeIcon={
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            color: theme.colors.textSecondary,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e0e0e0';
            e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f0f0f0';
            e.currentTarget.style.transform = 'rotate(0deg)';
          }}
          >
            ✕
          </div>
        }
      >
        <div style={{
          background: '#ffffff',
          borderRadius: theme.borderRadius.xl,
          padding: theme.spacing.xl,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <EmployeeForm
            form={editForm}
            onFinish={handleUpdate}
            submitText="Enregistrer les modifications"
            onCancel={() => {
              editForm.resetFields();
              setIsEditing(false);
              setCurrentEmployee(null);
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeListPage;
